<template>
  <article :class="['news-card cart-type-aquarist flex flex-col rounded-xxl w-full sm:w-74 h-full bg-light overflow-hidden transition-all duration-200 hover:shadow transform hover:-translate-y-0.5 border border-card-border hover:border-card-border-hover', className, `catalogue-card-${variant.id}`]">
    <div :class="[ 'relative flex items-center justify-center cursor-pointer w-auto', imageSize.class ]">
      <span class="sr-only">{{ $t('text-product-image') }}</span>
      <a
        :href="cataloguePostPath(variant)"
        class="block overflow-hidden absolute top-0 left-0 bottom-0 right-0 box-border m-0 text-light bg-radial-gradient"
        :aria-label="variant.name"
        @click.prevent="handleContentQuickView"
      >
        <Image
          :src="variant?.image?.url || _.get(variant, 'images.0.url', null) || siteSettings.product.placeholderImage[config.public.layoutDesign]"
          :alt="variant.name"
          layout="fill"
          object-fit="cover"
          :width="imageSize.width"
          :height="imageSize.height"
          sizes="294px"
          class-name="news-image"
          :loading="loading"
        />
      </a>
      <div class="pointer-events-none absolute inset-0 card-image-linear-gradient" />
      <div class="absolute top-0 pt-2.5 px-5 w-full flex justify-end">
        <CatalogueFavorite v-if="_.has(variant, 'isInCustomerFavorites')" :variant="variant" :accent="true" class="absolute top-2.5 end-5" />
      </div>
      <div v-if="_.has(variant, 'isInCustomerLikes')" class="absolute bottom-4 start-5 flex flex-row items-center justify-start space-x-2.5">
        <CatalogueViews :variant="variant" :accent="true" />
        <CatalogueLikes :variant="variant" :accent="true" />
        <MessagesBadge v-if="variant.commentsAllowed" :variant="variant" :accent="true" :on-click="openDetailsWithComments" />
      </div>
    </div>
    <header class="flex flex-col justify-between px-5 pt-4 pb-5 grow">
      <div class="flex flex-col justify-start">
        <a
          :href="cataloguePostPath(variant)"
          :aria-label="displayName"
          @click.prevent="handleContentQuickView"
        >
          <h3 class="line-clamp-3 leading-tight text-lg text-primary-dark font-bold overflow-ellipsis overflow-hidden cursor-pointer mb-1 hover:primary-dark-hover hover:underline focus:primary-dark-hover focus:underline" :title="displayName" v-text="displayName" />
        </a>
        <div v-if="ratingCount" class="inline-flex items-center justify-start gap-2 mb-2 overflow-hidden line-clamp-1">
          <div class="flex whitespace-nowrap" :aria-label="$t('text-catalogue-rating-average', { score: averageScore })">
            <component
              :is="value <= Math.round(averageScore) ? StarSolid : StarIcon"
              v-for="value in 5"
              :key="value"
              class="w-4 h-4 text-orange"
            />
          </div>
          <span class="text-xs text-body whitespace-nowrap">{{ averageScore }} ({{ ratingCount }})</span>
        </div>
        <div v-if="summaryText" class="w-full my-2 text-xs text-body font-normal line-clamp-5">
          {{ summaryText }}
        </div>
      </div>
      <div class="flex flex-col">
        <Scrollbar class="w-full" auto-hide="leave">
          <div class="w-full flex flex-nowrap pb-2" :style="windowSize?.width?.value < 480 ? { width: 'calc(100vw - 80px)' } : {}">
            <CatalogueTagParameters :data="variant" />
          </div>
        </Scrollbar>
        <div class="w-full flex items-center justify-end mt-auto">
          <Button v-if="onSelect" size="small" :on-click="() => onSelect(variant)">
            <span class="text-xs px-6">{{ $t('stock-button-select') }}</span>
          </Button>
          <Button v-else size="small" :on-click="handleContentQuickView">
            <span class="text-xs px-6">{{ $t('text-more') }}</span>
          </Button>
        </div>
      </div>
    </header>
  </article>
</template>

<script setup>
import _ from 'lodash-es';
import { siteSettings } from "@settings/site.settings";
import { useSelectedStore } from '@stores/selected';
import Image from "@components/ui/image";
import CatalogueFavorite from "@components/catalogue/catalogue-details/catalogue-favorite";
import CatalogueLikes from "@components/catalogue/catalogue-details/catalogue-likes";
import CatalogueViews from "@components/catalogue/catalogue-details/catalogue-views";
import MessagesBadge from "@components/common/messages-badge";
import CatalogueTagParameters from "@components/catalogue/catalogue-tag-parameters";
import StarIcon from "@components/icons/outline/star-icon";
import StarSolid from "@components/icons/solid/star-solid";
import Button from "@components/ui/button";
import Scrollbar from "@components/ui/scrollbar";

const props = defineProps({
  variant: {
    type: Object,
    required: true
  },
  className: {
    type: String,
    required: false,
    default: () => ''
  },
  loading: {
    type: String,
    default: null
  },
  // Replaces the "more" button with "select" (e.g. picking a product for the collection).
  onSelect: {
    type: Function,
    default: null
  }
})

const config = useRuntimeConfig();
const imageSize = siteSettings.catalogueImage[config.public.layoutDesign]?.card || siteSettings.catalogueImage.aquarist.card;
const store = useSelectedStore();
const { $eventBus } = useNuxtApp();
const windowSize = useWindowSize();

const displayName = computed(() => catalogueDisplayName(props.variant));
const summaryText = computed(() => localized(props.variant, 'perex') || plainText(localized(props.variant, 'description') || localized(props.variant, 'content'), 200));
const ratingCount = computed(() => props.variant?.ratingCount || props.variant?.ratings?.length || 0);
const averageScore = computed(() => {
  if (!ratingCount.value) {
    return '0.0';
  }
  if (props.variant?.ratingScore !== undefined && props.variant?.ratingScore !== null) {
    return Number(props.variant.ratingScore).toFixed(1);
  }
  const ratings = props.variant?.ratings || [];
  return ratings.length
    ? (ratings.reduce((total, rating) => total + rating.score, 0) / ratings.length).toFixed(1)
    : '0.0';
});

const openQuickView = (action = null) => {
  store.setAction(action);
  store.setContent(props.variant);
  $eventBus.emit('modal:open', 'CATALOGUE_DETAILS');
};

const handleContentQuickView = () => openQuickView();

const openDetailsWithComments = () => openQuickView('SELECT_TAB_COMMENTS');

</script>
